import { useState, useEffect } from "react";
import { TodayVideoMoment } from "@/components/VideoMoment";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, Check, X, Volume2, Mic, Square, Camera, Flame, Zap, ArrowRight, Trophy, Lock, Shield, GraduationCap } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { playAppAudio } from "@/lib/appAudio";
import { voiceOK, ttsOK } from "@/lib/languages";
import { useAuth } from "@/context/AuthContext";
import AppLayout from "@/components/AppLayout";
import Paywall from "@/components/Paywall";
import ConsentGate from "@/components/ConsentGate";
import Waveform from "@/components/Waveform";
import { useAudioRecorder } from "@/hooks/useAudioRecorder";
import { useI18n } from "@/context/I18nContext";
import { LifeCurriculum } from "@/components/LifeCurriculum";

export default function Today() {
  const { user, refresh } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const [plan, setPlan] = useState(null);
  const [phase, setPhase] = useState("intro"); // intro|review|speak|capture|done
  const [ri, setRi] = useState(0);
  const [revealed, setRevealed] = useState(false);
  const [results, setResults] = useState([]);
  const [finish, setFinish] = useState(null);
  const [paywall, setPaywall] = useState(false);
  const [consent, setConsent] = useState(false);
  const [scoreLoading, setScoreLoading] = useState(false);
  const [accentResult, setAccentResult] = useState(null);
  const [assignment, setAssignment] = useState(null);
  const [lcFailed, setLcFailed] = useState(false);
  const [accent, setAccent] = useState(null);
  const [hearing, setHearing] = useState(false);
  const [wordsDue, setWordsDue] = useState(0);
  const { recording, analyser, start, stop, audioMime } = useAudioRecorder();

  useEffect(() => { api.get("/today").then((r) => setPlan(r.data)).catch(() => {}); }, []);
  useEffect(() => { api.get("/today/assignment").then((r) => setAssignment(r.data.assignment)).catch(() => {}); }, []);
  // Approved AccentCoach phrase for the active profile; unsupported → no spoken step (never another language).
  useEffect(() => { api.get("/nudges").then((r) => setWordsDue(r.data.words_due || 0)).catch(() => {}); }, [user?.active_learning_profile_id]);
  useEffect(() => { api.get("/accent/phrases").then((r) => setAccent(r.data)).catch(() => setAccent({ supported: false })); }, []);

  const speakPhrase = accent?.supported ? accent.phrases?.[0] : null;
  const canSpeak = !!speakPhrase && voiceOK(user?.target_lang);
  const play = (path) => playAppAudio(path).catch((e) => { if (e?.response?.status === 402) setPaywall(true); else toast.error(t("review.voiceUnavailable")); });

  const steps = plan ? [
    plan.due_words.length ? "review" : null,
    canSpeak ? "speak" : null,
    plan.captured_today ? null : "capture",
  ].filter(Boolean) : [];

  const goNext = (from) => {
    const idx = steps.indexOf(from);
    const next = steps[idx + 1];
    setPhase(next || "finish");
  };

  useEffect(() => { if (phase === "finish") complete(); }, [phase]); // eslint-disable-line

  const complete = async () => {
    try {
      const res = await api.post("/today/complete");
      setFinish(res.data);
      await refresh();
    } catch { setFinish({ bonus_xp: 0 }); }
    setPhase("done");
  };

  // Life Curriculum session finished → keep the existing daily-loop completion (idempotent/day).
  const lcFinished = () => { api.post("/today/complete").then(() => refresh()).catch(() => {}); };
  const lcExtras = (srsDue) => (
    <div className="flex flex-col items-center gap-2" data-testid="lc-extras">
      <div className="text-xs font-bold uppercase tracking-widest text-gray-500">{t("lc.alsoToday")}</div>
      {srsDue > 0 && plan?.due_words?.length > 0 && <button onClick={() => { setRi(0); setPhase("review"); }} className="underline" data-testid="lc-review-all">{t("lc.reviewAll", { count: srsDue })}</button>}
      {canSpeak && <button onClick={() => setPhase("speak")} className="underline" data-testid="lc-say-aloud">{t("lc.sayAloud")}</button>}
      <Link to="/app" className="underline" data-testid="lc-snap">{t("lc.snapWord")}</Link>
    </div>
  );

  // ---- Review ----
  const answer = (correct) => {
    const word = plan.due_words[ri].word;
    const next = [...results, { word, correct }];
    setResults(next);
    setRevealed(false);
    if (ri + 1 < plan.due_words.length) setRi(ri + 1);
    else {
      api.post("/today/review", { results: next }).then(() => refresh()).catch(() => {});
      goNext("review");
    }
  };

  // ---- Speak ----
  const startRec = async () => {
    if (!plan.is_pro) { setPaywall(true); return; }
    if (!localStorage.getItem("fluent_consent_mic")) { setConsent(true); return; }
    try { setAccentResult(null); await start(); } catch { toast.error(t("today.micUnavailable")); }
  };
  const stopRec = async () => {
    const b64 = await stop();
    if (!b64) return;
    setScoreLoading(true);
    try {
      const res = await api.post("/accent/score", { audio: b64, mime: audioMime.current, phrase_id: speakPhrase.phrase_id });
      setAccentResult(res.data);
    } catch (e) { if (e?.response?.status === 402) setPaywall(true); else toast.error(t("today.scoringFailed")); }
    finally { setScoreLoading(false); }
  };

  if (!plan || !accent) return <AppLayout><div className="py-24 flex justify-center"><div className="w-12 h-12 border-4 border-ink border-t-coral rounded-full animate-spin" /></div></AppLayout>;

  return (
    <AppLayout>
      <div className="max-w-xl mx-auto px-4 py-8">
        <div className="flex items-center gap-2 mb-1">
          <Sparkles className="w-6 h-6 text-coral" />
          <h1 className="font-cabinet font-extrabold text-3xl">{t("today.title")}</h1>
        </div>
        {lcFailed ? <p className="text-gray-600">{t("today.subtitle")}</p> : (
          <div className="text-xs font-bold text-gray-500 flex items-center gap-1" data-testid="today-streak-mini"><Flame className="w-4 h-4" /> {t("today.streakText", { count: plan.streak_count })}</div>
        )}

        {wordsDue > 0 && phase === "intro" && (
          <button onClick={() => navigate("/review")} className="mt-3 nb-tag bg-lavender inline-flex items-center gap-1" data-testid="today-review-link">{t("review.todayLink", { count: wordsDue })} <ArrowRight className="w-3.5 h-3.5" /></button>
        )}

        {assignment && phase === "intro" && (
          <Link to={`/assignment/${assignment.assignment_id}`} className="block mt-5" data-testid="today-assignment-nudge">
            <div className="nb-card bg-coral text-white p-5 flex items-center justify-between hover:shadow-hard-lg transition-all">
              <div className="min-w-0">
                <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wide">
                  <GraduationCap className="w-4 h-4" />
                  <span data-testid="today-assigned-label">{t("lc.assigned")}</span> ·
                  {" "}{assignment.status === "in_progress" ? t("today.continueMission") : t("today.yourMission")}
                  {assignment.due_state && assignment.due_state !== "scheduled" && (
                    <span className="bg-white text-ink rounded px-1.5 py-0.5">{["due_soon", "overdue"].includes(assignment.due_state) ? t(`today.${assignment.due_state === "due_soon" ? "dueSoon" : "overdue"}`) : assignment.due_state}</span>
                  )}
                </div>
                <div className="font-cabinet font-extrabold text-xl mt-1 truncate">{assignment.title}</div>
                <div className="text-sm text-white/90">
                  {assignment.class_name} · {assignment.target_language?.toUpperCase()} {assignment.level}
                  {assignment.estimated_minutes ? ` · ${t("today.minutes", { count: assignment.estimated_minutes })}` : ""}
                  {assignment.status === "in_progress" && assignment.total_steps ? ` · ${t("today.stepsProgress", { done: assignment.completed_steps, total: assignment.total_steps })}` : ""}
                </div>
              </div>
              <span className="nb-tag bg-white text-ink shrink-0">{assignment.status === "in_progress" ? t("dash.continue") : t("dash.start")}</span>
            </div>
          </Link>
        )}

        {phase === "intro" && <TodayVideoMoment />}
        <AnimatePresence mode="wait">
          {/* INTRO */}
          {phase === "intro" && (
            <motion.div key="intro" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
              {!lcFailed ? (
                <LifeCurriculum onFinish={lcFinished} onUnavailable={() => setLcFailed(true)} extras={lcExtras} />
              ) : plan.completed_today ? (
                <div className="nb-card bg-mint p-6 mt-6 text-center" data-testid="today-already-done">
                  <div className="text-5xl">✅</div>
                  <h2 className="font-cabinet font-extrabold text-2xl mt-2">{t("today.loopCompleteToday")}</h2>
                  <p className="text-sm text-gray-700 mt-1">{t("today.comeBack")}</p>
                  <button onClick={() => { setPhase(steps[0] || "finish"); }} className="nb-btn-outline mt-5 px-6 py-3" data-testid="today-practice-again">{t("today.practiceAgain")}</button>
                </div>
              ) : (
                <>
                  <div className="nb-card bg-butter p-6 mt-6 flex items-center justify-between">
                    <div className="flex items-center gap-3"><Flame className="w-8 h-8" /><div><div className="font-cabinet font-extrabold text-3xl">{plan.streak_count}</div><div className="text-xs font-bold">{t("today.dayStreak", { count: plan.streak_count })}</div></div></div>
                    <div className="flex flex-col items-end gap-1">
                      <div className="flex items-center gap-2"><Zap className="w-6 h-6" /><span className="font-cabinet font-bold text-xl">{plan.xp} XP</span></div>
                      <div className="flex items-center gap-1 text-xs font-bold" data-testid="today-freeze"><Shield className="w-4 h-4" /> {t("dash.freezes", { count: plan.freeze_tokens ?? 0 })}</div>
                    </div>
                  </div>
                  <div className="space-y-3 mt-5">
                    {plan.due_words.length > 0 && <StepRow n="1" title={t("today.reviewWords")} sub={t("todayHelp.refresh")} bg="bg-lavender" icon={Check} />}
                    {canSpeak && <StepRow n={plan.due_words.length ? "2" : "1"} title={t("today.saySentence")} sub={plan.is_pro ? t("todayHelp.score") : t("todayHelp.proFeature")} bg="bg-peach" icon={Mic} locked={!plan.is_pro} />}
                    {!plan.captured_today && <StepRow n={(plan.due_words.length ? 1 : 0) + (canSpeak ? 1 : 0) + 1} title={t("today.captureNew")} sub={t("todayHelp.snap")} bg="bg-sky" icon={Camera} />}
                  </div>
                  <button onClick={() => setPhase(steps[0] || "finish")} className="nb-btn-primary w-full mt-6 py-4 text-lg" data-testid="today-start-btn">
                    {t("today.startLoop")} <ArrowRight className="w-5 h-5" />
                  </button>
                </>
              )}
            </motion.div>
          )}

          {/* REVIEW */}
          {phase === "review" && plan.due_words[ri] && (
            <motion.div key={"rev" + ri} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0 }} className="mt-6" data-testid="today-review">
              <div className="text-xs font-bold uppercase tracking-widest text-gray-400 mb-2">{t("today.wordProgress", { n: ri + 1, total: plan.due_words.length })}</div>
              <div className="nb-card bg-lavender p-8 text-center min-h-[220px] flex flex-col justify-center">
                <div className="flex items-center justify-center gap-2">
                  <div className="font-cabinet font-extrabold text-4xl" dir="auto">{plan.due_words[ri].word}</div>
                  {ttsOK(user?.target_lang) && <button onClick={() => play(`/vocab/${plan.due_words[ri].id}/audio`)} data-testid="today-word-listen" className="w-9 h-9 bg-white border-2 border-ink rounded-full flex items-center justify-center"><Volume2 className="w-4 h-4" /></button>}
                </div>
                {revealed ? (
                  <div className="mt-4">
                    <div className="text-lg font-bold" dir="auto">{plan.due_words[ri].translation}</div>
                    <div className="text-sm text-gray-600 italic mt-1" dir="auto">"{plan.due_words[ri].example_sentence}"</div>
                  </div>
                ) : (
                  <button onClick={() => setRevealed(true)} className="nb-btn-outline mt-5 mx-auto px-6 py-2" data-testid="today-reveal">{t("today.reveal")}</button>
                )}
              </div>
              {revealed && (
                <div className="flex gap-3 mt-4">
                  <button onClick={() => answer(false)} className="nb-btn-outline flex-1 py-3" data-testid="today-missed"><X className="w-5 h-5" /> {t("today.missedIt")}</button>
                  <button onClick={() => answer(true)} className="nb-btn-mint flex-1 py-3" data-testid="today-got"><Check className="w-5 h-5" /> {t("today.gotIt")}</button>
                </div>
              )}
            </motion.div>
          )}

          {/* SPEAK */}
          {phase === "speak" && speakPhrase && (
            <motion.div key="speak" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0 }} className="mt-6" data-testid="today-speak">
              <div className="nb-card bg-peach p-6 text-center">
                <div className="nb-tag bg-white mb-3">{t("today.sayItOut")}</div>
                <p className="font-cabinet font-extrabold text-2xl" dir="auto" data-testid="today-speak-phrase">{speakPhrase.text}</p>
                {plan.is_pro && ttsOK(user?.target_lang) && <button onClick={() => { setHearing(true); play(`/accent/phrases/${speakPhrase.phrase_id}/audio`).finally(() => setHearing(false)); }} disabled={hearing} data-testid="today-hear-phrase" className="nb-btn-outline mt-3 px-4 py-2 text-sm disabled:opacity-60"><Volume2 className="w-4 h-4" /> {t("today.hearIt")}</button>}
              </div>
              {!plan.is_pro ? (
                <div className="nb-card p-6 mt-4 text-center">
                  <Lock className="w-8 h-8 mx-auto" />
                  <p className="font-cabinet font-bold mt-2">{t("todayHelp.proScoring")}</p>
                  <button onClick={() => setPaywall(true)} className="nb-btn-secondary mt-3 px-6 py-2">{t("today.unlockPro")}</button>
                  <button onClick={() => goNext("speak")} className="block w-full text-sm text-gray-500 mt-3">{t("today.skipForNow")}</button>
                </div>
              ) : accentResult ? (
                <div className="nb-card p-5 mt-4">
                  <div className="text-sm"><b>{t("today.weHeard")}</b> <span dir="auto">"{accentResult.transcript}"</span></div>
                  <div className="flex flex-wrap gap-2 mt-3">{Object.entries(accentResult.scores || {}).map(([k, v]) => <span key={k} className="nb-tag bg-mint">{k}: {v}</span>)}</div>
                  <button onClick={() => goNext("speak")} className="nb-btn-primary w-full mt-4 py-3">{t("today.continue")} <ArrowRight className="w-5 h-5" /></button>
                </div>
              ) : (
                <div className="flex flex-col items-center mt-6">
                  {scoreLoading ? <div className="w-16 h-16 border-4 border-ink border-t-coral rounded-full animate-spin" /> : recording ? (
                    <button onClick={stopRec} className="w-20 h-20 bg-coral border-4 border-ink rounded-full shadow-hard flex items-center justify-center animate-pulse" data-testid="today-stop-rec"><Square className="w-8 h-8 text-white fill-white" /></button>
                  ) : (
                    !voiceOK(user?.target_lang) ? null : <button onClick={startRec} className="w-20 h-20 bg-mint border-4 border-ink rounded-full shadow-hard flex items-center justify-center" data-testid="today-record"><Mic className="w-9 h-9 stroke-[2.5]" /></button>
                  )}
                  {recording && <div className="nb-card p-3 mt-4 w-full"><Waveform analyser={analyser} active color="#FF6B6B" height={60} bars={36} /></div>}
                  {!recording && !scoreLoading && <button onClick={() => goNext("speak")} className="text-sm text-gray-500 mt-4">{t("today.skip")}</button>}
                </div>
              )}
            </motion.div>
          )}

          {/* CAPTURE */}
          {phase === "capture" && (
            <motion.div key="capture" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0 }} className="mt-6" data-testid="today-capture">
              <div className="nb-card bg-sky p-8 text-center">
                <Camera className="w-12 h-12 mx-auto stroke-[2]" />
                <h3 className="font-cabinet font-bold text-xl mt-3">{t("today.learnNewWord")}</h3>
                <p className="text-sm text-gray-700 mt-1">{t("today.snapHint")}</p>
                <Link to="/app" className="nb-btn-primary mt-5 px-6 py-3 inline-flex" data-testid="today-goto-capture"><Camera className="w-5 h-5" /> {t("today.openCamera")}</Link>
                <button onClick={() => goNext("capture")} className="block w-full text-sm text-gray-500 mt-3">{t("today.doLater")}</button>
              </div>
            </motion.div>
          )}

          {/* DONE */}
          {phase === "done" && (
            <motion.div key="done" initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="nb-card bg-mint p-8 mt-6 text-center" data-testid="today-done">
              <div className="text-6xl">🎉</div>
              <h2 className="font-cabinet font-extrabold text-3xl mt-2">{t("today.loopComplete")}</h2>
              {finish?.bonus_xp > 0 && <p className="font-bold mt-1">{t("today.bonusXp", { count: finish.bonus_xp })}</p>}
              <div className="flex items-center justify-center gap-2 mt-3"><Flame className="w-6 h-6" /><span className="font-cabinet font-bold text-xl">{t("today.streakText", { count: finish?.streak_count || plan.streak_count })}</span></div>
              <button onClick={() => navigate("/dashboard")} className="nb-btn-primary mt-6 px-8 py-3">{t("today.backToDashboard")}</button>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
      <ConsentGate type="mic" open={consent} onGrant={() => { setConsent(false); startRec(); }} onClose={() => setConsent(false)} />
      <Paywall open={paywall} onClose={() => setPaywall(false)} reason={t("today.paywallReason")} />
    </AppLayout>
  );
}

function StepRow({ n, title, sub, bg, icon: Icon, locked }) {
  return (
    <div className={`nb-card ${bg} p-4 flex items-center gap-4`}>
      <div className="w-10 h-10 rounded-lg border-2 border-ink bg-white flex items-center justify-center font-cabinet font-extrabold">{n}</div>
      <div className="flex-1">
        <div className="font-cabinet font-bold flex items-center gap-1">{title} {locked && <Lock className="w-3.5 h-3.5" />}</div>
        <div className="text-xs text-gray-600">{sub}</div>
      </div>
      <Icon className="w-6 h-6 stroke-[2.5]" />
    </div>
  );
}
